/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import { FeatureFlag, isFeatureEnabled } from '@aibi-ui/core';
import { lazy } from 'react';
import mobileRoutes from 'src/mobile/routes/index';
import backStageRoutes from 'src/pages/Backstage/routes';

// not lazy loaded since this is the home page.
import Home from 'src/pages/Home';
import Login from 'src/pages/Login';
import { EnumBehaviorKey } from 'src/types/Behaviors';
import { AppRoute } from 'src/types/Route';
import { defineRoute } from 'src/utils/route';

const ChartCreation = lazy(
  () =>
    import(/* webpackChunkName: "ChartCreation" */ 'src/pages/ChartCreation'),
);

const AnnotationLayerList = lazy(
  () =>
    import(
      /* webpackChunkName: "AnnotationLayerList" */ 'src/pages/AnnotationLayerList'
    ),
);

const AlertReportList = lazy(
  () =>
    import(
      /* webpackChunkName: "AlertReportList" */ 'src/pages/AlertReportList'
    ),
);

const AnnotationList = lazy(
  () =>
    import(/* webpackChunkName: "AnnotationList" */ 'src/pages/AnnotationList'),
);

const ChartList = lazy(
  () => import(/* webpackChunkName: "ChartList" */ 'src/pages/ChartList'),
);

const CssTemplateList = lazy(
  () =>
    import(
      /* webpackChunkName: "CssTemplateList" */ 'src/pages/CssTemplateList'
    ),
);

const DashboardList = lazy(
  () =>
    import(/* webpackChunkName: "DashboardList" */ 'src/pages/DashboardList'),
);

const Dashboard = lazy(
  () => import(/* webpackChunkName: "Dashboard" */ 'src/pages/Dashboard'),
);

const DatabaseList = lazy(
  () => import(/* webpackChunkName: "DatabaseList" */ 'src/pages/DatabaseList'),
);

const DatasetList = lazy(
  () => import(/* webpackChunkName: "DatasetList" */ 'src/pages/DatasetList'),
);

// const DatasetCreation = lazy(
//   () =>
//     import(
//       /* webpackChunkName: "DatasetCreation" */ 'src/pages/DatasetCreation'
//     ),
// );

const DatasetDetail = lazy(
  () =>
    import(/* webpackChunkName: "DatasetDetail" */ 'src/pages/DatasetDetail'),
);

const ExecutionLogList = lazy(
  () =>
    import(
      /* webpackChunkName: "ExecutionLogList" */ 'src/pages/ExecutionLogList'
    ),
);

const Chart = lazy(
  () => import(/* webpackChunkName: "Chart" */ 'src/pages/Chart'),
);

const ChartPreview = lazy(
  () => import(/* webpackChunkName: "ChartPreview" */ 'src/pages/ChartPreview'),
);

const QueryHistoryList = lazy(
  () =>
    import(
      /* webpackChunkName: "QueryHistoryList" */ 'src/pages/QueryHistoryList'
    ),
);

const SavedQueryList = lazy(
  () =>
    import(/* webpackChunkName: "SavedQueryList" */ 'src/pages/SavedQueryList'),
);

const SqlLab = lazy(
  () => import(/* webpackChunkName: "SqlLab" */ 'src/pages/SqlLab'),
);

const AllEntities = lazy(
  () => import(/* webpackChunkName: "AllEntities" */ 'src/pages/AllEntities'),
);

const Tags = lazy(
  () => import(/* webpackChunkName: "Tags" */ 'src/pages/Tags'),
);

const RowLevelSecurityList = lazy(
  () =>
    import(
      /* webpackChunkName: "RowLevelSecurityList" */ 'src/pages/RowLevelSecurityList'
    ),
);

const Conversation = lazy(
  () => import(/* webpackChunkName: "Conversation" */ 'src/pages/Conversation'),
);

const Backstage = lazy(
  () => import(/* webpackChunkName: "Backstage" */ 'src/pages/Backstage'),
);

const ConversationShare = lazy(
  () =>
    import(
      /* webpackChunkName: "ConversationShare" */ 'src/pages/ConversationShare'
    ),
);

const Mobile = lazy(
  () => import(/* webpackChunkName: "Mobile" */ 'src/mobile/layout/index'),
);

export const routes: AppRoute[] = [
  {
    path: '/aibi/login',
    Component: Login,
  },
  {
    path: '/aibi/welcome/',
    Component: Home,
  },
  {
    path: '/dashboard/list/',
    Component: DashboardList,
  },
  {
    path: '/aibi/dashboard/:idOrSlug/',
    Component: Dashboard,
  },
  {
    path: '/dashboard-preview',
    Component: Dashboard,
  },
  {
    path: '/chart/add',
    Component: ChartCreation,
  },
  {
    path: '/chart/list/',
    Component: ChartList,
  },
  {
    path: '/tablemodelview/list/',
    Component: DatasetList,
  },
  {
    path: '/databaseview/list/',
    Component: DatabaseList,
  },
  {
    path: '/savedqueryview/list/',
    Component: SavedQueryList,
  },
  {
    path: '/csstemplatemodelview/list/',
    Component: CssTemplateList,
  },
  {
    path: '/annotationlayer/list/',
    Component: AnnotationLayerList,
  },
  {
    path: '/annotationlayer/:annotationLayerId/annotation/',
    Component: AnnotationList,
  },
  {
    path: '/sqllab/history/',
    Component: QueryHistoryList,
  },
  {
    path: '/alert/list/',
    Component: AlertReportList,
  },
  defineRoute({
    path: '/report/list/',
    Component: AlertReportList,
    props: {
      isReportEnabled: true,
    },
  }),
  {
    path: '/alert/:alertId/log/',
    Component: ExecutionLogList,
  },
  defineRoute({
    path: '/report/:alertId/log/',
    Component: ExecutionLogList,
    props: {
      isReportEnabled: true,
    },
  }),
  {
    path: '/explore/',
    Component: Chart,
  },
  {
    path: '/aibi/explore/p',
    Component: Chart,
  },
  {
    path: '/chart-preview',
    Component: ChartPreview,
  },
  // {
  //   path: '/dataset/add/',
  //   Component: DatasetCreation,
  // },
  // {
  //   path: '/dataset/:datasetId',
  //   Component: DatasetCreation,
  // },
  {
    path: '/dataset/',
    Component: DatasetDetail,
  },
  {
    path: '/rowlevelsecurity/list',
    Component: RowLevelSecurityList,
  },
  {
    path: '/sqllab/',
    Component: SqlLab,
  },
  {
    path: '/analyze-report/chat',
    Component: lazy(
      () =>
        import(
          /* webpackChunkName: "AnalyzeReportChat" */ 'src/pages/AnalyzeReport/Chat'
        ),
    ),
    behaviors: [EnumBehaviorKey.ANALYZE_REPORT],
  },
  {
    path: '/analyze-report/list',
    Component: lazy(
      () =>
        import(
          /* webpackChunkName: "AnalyzeReportList" */ 'src/pages/AnalyzeReport/List'
        ),
    ),
    behaviors: [EnumBehaviorKey.ANALYZE_REPORT],
  },
  {
    path: '/analyze-report/detail',
    Component: lazy(
      () =>
        import(
          /* webpackChunkName: "AnalyzeReportDetail" */ 'src/pages/AnalyzeReport/List/subPages/Detail'
        ),
    ),
  },
  {
    path: '/topic-share/public',
    Component: lazy(
      () =>
        import(
          /* webpackChunkName: "TopicSharePublic" */ 'src/pages/TopicShare/public'
        ),
    ),
  },
  {
    path: '/topic-share/topic',
    Component: lazy(
      () =>
        import(
          /* webpackChunkName: "TopicShareTopic" */ 'src/pages/TopicShare/topic'
        ),
    ),
  },
  {
    path: '/chat',
    Component: Conversation,
  },
  {
    path: '/conversation-share',
    Component: ConversationShare,
  },
  defineRoute({
    path: '/mobile',
    Component: Mobile,
    props: { routes: mobileRoutes },
  }),
  defineRoute({
    path: '/backstage',
    Component: Backstage,
    props: { routes: backStageRoutes },
  }),
  {
    path: '/user',
    Component: lazy(
      () => import(/* webpackChunkName: "UserInfo" */ 'src/pages/User'),
    ),
  },
];

if (isFeatureEnabled(FeatureFlag.TaggingSystem)) {
  routes.push({
    path: '/aibi/all_entities/',
    Component: AllEntities,
  });
  routes.push({
    path: '/aibi/tags/',
    Component: Tags,
  });
}

const frontEndRoutes = routes
  .map(r => r.path)
  .reduce(
    (acc, curr) => ({
      ...acc,
      [curr]: true,
    }),
    {} as Record<string, boolean>,
  );

export function isFrontendRoute(path?: string) {
  if (path) {
    const basePath = path.split(/[?#]/)[0]; // strip out query params and link bookmarks
    return !!frontEndRoutes[basePath];
  }
  return false;
}
